/**
 * /admin/customers/insights — Q&A semantic clustering + downvote feed.
 * Lets the admin "understand key questions and responses by organizations and individual users".
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Lightbulb, ThumbsDown, MessageSquare, Cpu, ExternalLink, Layers,
} from 'lucide-react';
import { adminCustomersApi } from '../../services/api';
import { toast } from 'sonner';

const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString());
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString() : '—');

const AdminCustomerInsights = () => {
  const navigate = useNavigate();
  const [scope, setScope] = useState('all');
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState({});  // id -> name

  useEffect(() => {
    setLoading(true);
    adminCustomersApi.qaInsights({ scope, days })
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load insights'))
      .finally(() => setLoading(false));
  }, [scope, days]);

  useEffect(() => {
    // Hydrate tenant names once for downvote attribution
    adminCustomersApi.list({ limit: 10000 })
      .then(({ data }) => {
        const m = {};
        for (const t of (data.items || [])) m[t.id] = { name: t.name, type: t.account_type };
        setTenants(m);
      })
      .catch(() => {});
  }, []);

  const totalConsumed = (data?.model_split || []).reduce((s, m) => s + (m.credits || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-insights">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/admin/customers')}
            className="p-1.5 rounded-lg hover:bg-white/5 text-nexus-text-tertiary"
            data-testid="back-to-customers-btn">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>SEMANTIC INSIGHTS</div>
            <h1 className="font-heading text-3xl font-semibold text-nexus-text-primary mt-1 inline-flex items-center gap-2">
              <Lightbulb size={22} /> Questions &amp; responses
            </h1>
            <p className="text-[12px] text-nexus-text-tertiary mt-1">
              Top user questions clustered by similarity (TF-IDF cosine), plus every downvoted response.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
            {['all', 'organization', 'personal'].map(s => (
              <button key={s} onClick={() => setScope(s)}
                className="px-2.5 h-8 rounded-md text-[11px] capitalize transition-all"
                style={{
                  background: scope === s ? 'var(--layer-0)' : 'transparent',
                  color:      scope === s ? 'var(--nexus-text-primary)' : 'var(--nexus-text-tertiary)',
                  fontWeight: scope === s ? 600 : 400,
                }}
                data-testid={`scope-${s}`}>{s}</button>
            ))}
          </div>
          <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
            {[30, 90, 180].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className="px-2.5 h-8 rounded-md text-[11px] transition-all"
                style={{
                  background: days === d ? 'var(--layer-0)' : 'transparent',
                  color:      days === d ? 'var(--nexus-text-primary)' : 'var(--nexus-text-tertiary)',
                  fontWeight: days === d ? 600 : 400,
                }}
                data-testid={`days-${d}`}>{d}d</button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="text-nexus-text-tertiary">Crunching {data?.sample_size ?? '…'} questions…</div>}

      {data && !loading && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="insight-summary">
            <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
              <div className="text-[10px] font-semibold tracking-wider uppercase mb-1" style={{ color: '#F26B1F' }}>Sample size</div>
              <div className="font-heading text-2xl font-semibold text-nexus-text-primary">{fmtNum(data.sample_size)}</div>
              <div className="text-[11px] text-nexus-text-tertiary mt-0.5">first user message / convo</div>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
              <div className="text-[10px] font-semibold tracking-wider uppercase mb-1" style={{ color: '#F26B1F' }}>Topic clusters</div>
              <div className="font-heading text-2xl font-semibold text-nexus-text-primary">{(data.clusters || []).length}</div>
              <div className="text-[11px] text-nexus-text-tertiary mt-0.5">distinct intents</div>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
              <div className="text-[10px] font-semibold tracking-wider uppercase mb-1" style={{ color: '#F26B1F' }}>Downvotes</div>
              <div className="font-heading text-2xl font-semibold text-nexus-text-primary">{(data.downvoted || []).length}</div>
              <div className="text-[11px] text-nexus-text-tertiary mt-0.5">flagged responses</div>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
              <div className="text-[10px] font-semibold tracking-wider uppercase mb-1" style={{ color: '#F26B1F' }}>Credits used</div>
              <div className="font-heading text-2xl font-semibold text-nexus-text-primary">{Math.round(totalConsumed).toLocaleString()}</div>
              <div className="text-[11px] text-nexus-text-tertiary mt-0.5">{data.days}-day window</div>
            </div>
          </div>

          {/* Top question clusters */}
          <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="top-clusters">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>TOP QUESTION CLUSTERS</div>
              <div className="text-[10px] text-nexus-text-tertiary inline-flex items-center gap-1"><Layers size={11}/> grouped by semantic similarity</div>
            </div>
            <div className="space-y-2.5">
              {(data.clusters || []).length === 0 && (
                <div className="text-nexus-text-tertiary text-[12px]">Not enough data — clusters appear once users have asked questions.</div>
              )}
              {(data.clusters || []).map((c, i) => {
                const max = (data.clusters[0] || {}).count || 1;
                return (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
                    className="rounded-lg p-3" style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
                    data-testid={`cluster-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(242,107,31,0.10)', color: '#F26B1F' }}>#{i + 1}</span>
                          <span className="text-[14px] text-nexus-text-primary font-medium leading-tight truncate">{c.label}</span>
                        </div>
                        {c.exemplars.length > 1 && (
                          <ul className="ml-1 mt-1 space-y-0.5 text-[11px] text-nexus-text-tertiary">
                            {c.exemplars.slice(1, 4).map((e, j) => (
                              <li key={j} className="truncate">↳ {e}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-heading text-xl font-semibold text-nexus-text-primary">{c.count}</div>
                        <div className="text-[10px] text-nexus-text-tertiary uppercase tracking-wider">asks</div>
                      </div>
                    </div>
                    <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <div className="h-full" style={{ width: `${(c.count / max) * 100}%`, background: '#F26B1F' }}/>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Model split + downvotes */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.4fr] gap-3">
            <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="model-split-table">
              <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>MODEL SPLIT</div>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                    <th className="text-left py-1.5">Model</th>
                    <th className="text-right py-1.5">Calls</th>
                    <th className="text-right py-1.5">Avg in</th>
                    <th className="text-right py-1.5">Avg out</th>
                    <th className="text-right py-1.5">Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.model_split || []).map((m) => (
                    <tr key={m._id || 'unknown'} className="border-t" style={{ borderColor: 'var(--hairline)' }}>
                      <td className="py-2 text-nexus-text-secondary"><Cpu size={11} className="inline mr-1 text-nexus-text-tertiary"/>{m._id || 'unknown'}</td>
                      <td className="py-2 text-right font-mono">{fmtNum(m.calls)}</td>
                      <td className="py-2 text-right font-mono text-nexus-text-tertiary">{Math.round(m.avg_input || 0)}</td>
                      <td className="py-2 text-right font-mono text-nexus-text-tertiary">{Math.round(m.avg_output || 0)}</td>
                      <td className="py-2 text-right font-mono text-nexus-text-primary">{(m.credits || 0).toFixed(1)}</td>
                    </tr>
                  ))}
                  {(data.model_split || []).length === 0 && (
                    <tr><td colSpan="5" className="py-6 text-center text-nexus-text-tertiary">No usage in this scope yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="rounded-xl p-5" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="downvotes-list">
              <div className="text-[10px] font-semibold tracking-wider uppercase mb-3 inline-flex items-center gap-1" style={{ color: '#F26B1F' }}>
                <ThumbsDown size={11} /> RECENT DOWNVOTES
              </div>
              <div className="space-y-2 max-h-[360px] overflow-y-auto">
                {(data.downvoted || []).length === 0 && (
                  <div className="text-nexus-text-tertiary text-[12px]">No downvotes yet 🎉</div>
                )}
                {(data.downvoted || []).map((d, i) => {
                  const ten = tenants[d.tenant_id];
                  return (
                    <div key={i} className="rounded-lg p-3"
                      style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
                      data-testid={`downvote-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">
                          {ten?.name || 'Unknown tenant'}{ten?.type === 'personal' ? ' · personal' : ''} · {d.model}
                        </span>
                        <button onClick={() => navigate(`/admin/customers/${d.tenant_id}`)}
                          className="text-nexus-text-tertiary hover:text-nexus-text-primary"
                          aria-label="Open tenant"
                          data-testid={`goto-tenant-${i}`}>
                          <ExternalLink size={11}/>
                        </button>
                      </div>
                      <p className="text-[12px] text-nexus-text-secondary line-clamp-3">{d.snippet || '(empty response)'}</p>
                      {d.comment && (
                        <p className="mt-1.5 text-[11px] text-red-400/90 italic">"{d.comment}"</p>
                      )}
                      <div className="mt-1.5 text-[10px] text-nexus-text-tertiary">{fmtDateTime(d.created_at)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCustomerInsights;
